/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2021 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { ColorPickerFieldDefinition, LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button from "@insite/mobius/Button";
import LazyImage from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Page, { PageProps } from "@insite/mobius/Page";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { getProp } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { screen } from "@monsma/XDL/Helpers";
import { color, mergeWithCss } from "@monsma/XDL/Utilities";
import { hideScrollBar } from "@monsma/XDL/Utilities/sharedStyles";
import React from "react";
import { css } from "styled-components";

const enum fields {
    checkboxFieldValue = "checkboxFieldValue",
    title1 = "title1",
    imageUrl1 = "imageUrl1",
    text1 = "text1",
    buttonText1 = "buttonText1",
    link1 = "link1",
    title2 = "title2",
    imageUrl2 = "imageUrl2",
    text2 = "text2",
    buttonText2 = "buttonText2",
    link2 = "link2",
    makeImagesMoreTallThanWide = "makeImagesMoreTallThanWide",
    backgroundColor = "backgroundColor",
    addShadow = "addShadow",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.checkboxFieldValue]: boolean;
        [fields.title1]: string;
        [fields.imageUrl1]: string;
        [fields.text1]: string;
        [fields.buttonText1]: string;
        [fields.link1]: LinkFieldValue;
        [fields.title2]: string;
        [fields.imageUrl2]: string;
        [fields.text2]: string;
        [fields.buttonText2]: string;
        [fields.link2]: LinkFieldValue;
        [fields.makeImagesMoreTallThanWide]: boolean;
        [fields.backgroundColor]: ColorPickerFieldDefinition;
        [fields.addShadow]: boolean;
    };
}

type Props = OwnProps;

const gradientStyle = css`
    position: absolute;
    top: 0;
    height: 100%;
    width: 10%;
    z-index: ${getProp("theme.zIndex.default")};

    ${screen("lte", "md")} {
        display: none;
    }
`;

const contentWrapper = css`
    box-sizing: border-box;
    width: 478px;
    min-height: 240px;
    border: 1px solid ${getColor("common.ui12")};
    border-radius: 6px;
    background-color: ${getColor("common.background")};
    position: absolute;
    bottom: -60px;
    z-index: ${getProp("theme.zIndex.level1")};
    padding: 20px 20px 25px 20px;
    display: flex;
    flex-direction: column;
    align-items: flex-start;

    &.centered {
        bottom: unset;
        top: 50%;
        transform: translateY(-50%);
    }

    ${screen("lte", "1185")} {
        &&& {
            bottom: unset;
            right: unset;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            width: calc(100% - 32px);
        }
    }

    ${screen("only", "md")} {
        &&& {
            width: calc(100% - 48px);
        }
    }

    ${screen("lte", "sm")} {
        &&& {
            min-height: unset;
        }
    }
`;

export interface WhyMonsmaWidgetStyles {
    wrapper?: InjectableCss;
    imageLink?: LinkPresentationProps;
    imageWrapper?: InjectableCss;
    leftContentWrapper?: InjectableCss;
    rightContentWrapper?: InjectableCss;
    sectionWrapper?: InjectableCss;
    container?: PageProps;
    sectionTitle?: TypographyProps;
    sectionBody?: InjectableCss;
    textContainer?: TypographyProps;
}

export const whyMonsmaWidgetStyle: WhyMonsmaWidgetStyles = {
    wrapper: {
        css: css`
            background: ${({ backgroundColor }: any) => backgroundColor};

            &.layout1 {
                padding: 60px 0;
                box-shadow: ${({ shadow }: any) => shadow && getProp("theme.shadows.1")};
                background: ${({ backgroundColor }: any) => backgroundColor};
            }
        `,
    },
    imageLink: {
        css: css`
            display: block;
            width: 100%;
            height: 100%;

            * {
                width: 100%;
                height: 100%;
                padding: 0;
            }
        `,
    },
    imageWrapper: {
        css: css`
            overflow: hidden;

            &:not(.layout1) {
                height: 100%;
            }

            &.layout1 {
                width: 150px;
                height: 300px;
                flex: 0 0 auto;
                margin-right: 40px;
                border-radius: 4px;
                border: 1px solid ${getColor("common.ui4")};
                display: flex;
                align-items: center;
                justify-content: center;

                ${screen("lt", "sm")} {
                    width: 100%;
                    height: auto;
                    margin-right: 0;
                    margin-bottom: 15px;
                }
            }

            &&& {
                img {
                    object-fit: cover;
                    object-position: center center;
                    height: 100%;
                }
            }

            &.layout1 {
                ${screen("lte", "sm")} {
                    display: none;
                }
            }
        `,
    },
    leftContentWrapper: {
        css: css`
            &:not(.layout1) {
                ${contentWrapper};

                ${screen("gt", "md")} {
                    right: 66px;
                }
            }
        `,
    },
    rightContentWrapper: {
        css: css`
            &:not(.layout1) {
                ${contentWrapper};

                ${screen("gt", "md")} {
                    left: 66px;
                    right: unset;
                }
            }
        `,
    },
    sectionWrapper: {
        css: css`
            width: 50%;

            &:not(.layout1) {
                height: 500px;
                position: relative;

                ${screen("lte", "sm")} {
                    width: 100%;
                }
            }

            &.layout1 {
                display: flex;
                align-items: center;
                padding-right: 30px;

                &:last-child {
                    padding-right: 0;
                    padding-left: 33px;
                }

                ${screen("lt", "sm")} {
                    flex-direction: column;
                }

                ${screen("lt", "md")} {
                    width: 100%;
                    padding: 0 !important;

                    &:first-child {
                        margin-bottom: 80px;
                    }
                }
            }
        `,
    },
    container: {
        as: "div",
        background: "transparent",
        css: css`
            display: flex;

            &:not(.layout1) {
                padding: 0;
                position: relative;

                &:after {
                    content: " ";
                    left: 0;
                    ${gradientStyle};
                    background-image: linear-gradient(
                        to right,
                        ${props => color((props as any).backgroundColor, 1)} 0%,
                        ${props => color((props as any).backgroundColor, 0.3)} 50%,
                        ${props => color((props as any).backgroundColor, 0.15)} 65%,
                        ${props => color((props as any).backgroundColor, 0.075)} 75.5%,
                        ${props => color((props as any).backgroundColor, 0.037)} 82.85%,
                        ${props => color((props as any).backgroundColor, 0.019)} 88%,
                        transparent 100%
                    );
                }

                &:before {
                    content: " ";
                    right: 0;
                    ${gradientStyle};
                    background-image: linear-gradient(
                        to left,
                        ${props => color((props as any).backgroundColor, 1)} 0%,
                        ${props => color((props as any).backgroundColor, 0.3)} 50%,
                        ${props => color((props as any).backgroundColor, 0.15)} 65%,
                        ${props => color((props as any).backgroundColor, 0.075)} 75.5%,
                        ${props => color((props as any).backgroundColor, 0.037)} 82.85%,
                        ${props => color((props as any).backgroundColor, 0.019)} 88%,
                        transparent 100%
                    );
                }

                ${screen("gt", "xl")} {
                    max-width: 1366px;
                }

                ${screen("lte", "sm")} {
                    flex-direction: column;
                }
            }

            &.layout1 {
                ${screen("lt", "md")} {
                    flex-direction: column;
                }
            }
        `,
    },
    sectionBody: {
        css: css`
            ${screen("gte", "md")} {
                overflow-y: auto;
                height: 78px;
                ${hideScrollBar};
            }

            &:not(.layout1) {
                margin-bottom: 20px;
            }

            &.layout1 {
                margin-bottom: 25px;
            }
        `,
    },
    textContainer: {
        css: css`
            line-height: 26px;
            margin-bottom: 0;

            &.layout1 {
                color: ${getColor("text.inverse")};
            }
        `,
    },
    sectionTitle: {
        css: css`
            &.layout1 {
                color: ${getColor("text.inverse")};
                line-height: 28px;
                margin-bottom: 19px;
            }
        `,
    },
};

const styles = whyMonsmaWidgetStyle;

const WhyMonsma: React.FC<Props> = ({ fields }) => {
    const history = useHistory();
    const url1 = useGetLink(fields.link1).url;
    const url2 = useGetLink(fields.link2).url;
    const image1 = <LazyImage src={fields.imageUrl1} altText={fields.title1} />;
    const image2 = <LazyImage src={fields.imageUrl2} altText={fields.title2} />;
    const isMakeImagesMoreTallThanWide = fields.makeImagesMoreTallThanWide;

    const clickHandler = (link: any) => {
        if (!link) {
            return;
        }

        if (link.startsWith("http")) {
            window.location.href = link;
        } else {
            history.push(link);
        }
    };

    const getSection = (image: any, url: any, title: any, text: any, buttonText: any, position: any) => {
        const hasValue = Boolean(title || text || buttonText);

        return (
            <StyledWrapper {...styles.sectionWrapper} className={isMakeImagesMoreTallThanWide ? "layout1" : ""}>
                <StyledWrapper {...styles.imageWrapper} className={isMakeImagesMoreTallThanWide ? "layout1" : ""}>
                    {url1 ? (
                        <Link href={url} {...styles.imageLink}>
                            {image}
                        </Link>
                    ) : (
                        image
                    )}
                </StyledWrapper>
                {hasValue && (
                    <StyledWrapper
                        {...mergeWithCss(
                            styles.leftContentWrapper,
                            position === "right" ? styles.rightContentWrapper : {},
                        )}
                        {...{ backgroundColor: fields.backgroundColor }}
                        className={
                            isMakeImagesMoreTallThanWide ? "layout1" : fields.checkboxFieldValue ? "centered" : ""
                        }
                    >
                        {title && (
                            <Typography
                                variant="h3"
                                as="h2"
                                data-test-selector="sectionTitle"
                                {...styles.sectionTitle}
                                className={isMakeImagesMoreTallThanWide ? "layout1" : ""}
                            >
                                {title}
                            </Typography>
                        )}
                        {text && (
                            <StyledWrapper
                                as="div"
                                {...styles.sectionBody}
                                className={isMakeImagesMoreTallThanWide ? "layout1" : ""}
                            >
                                <Typography
                                    variant="p"
                                    data-test-selector="sectionBody"
                                    {...styles.textContainer}
                                    className={isMakeImagesMoreTallThanWide ? "layout1" : ""}
                                >
                                    {text}
                                </Typography>
                            </StyledWrapper>
                        )}
                        {buttonText && (
                            <Button
                                variant={isMakeImagesMoreTallThanWide ? "tertiary" : "primary"}
                                sizeVariant={isMakeImagesMoreTallThanWide ? "small" : "large"}
                                onClick={() => clickHandler(url)}
                            >
                                {buttonText}
                            </Button>
                        )}
                    </StyledWrapper>
                )}
            </StyledWrapper>
        );
    };

    return (
        <>
            <StyledWrapper
                {...styles.wrapper}
                className={isMakeImagesMoreTallThanWide ? "layout1" : ""}
                {...{ backgroundColor: fields.backgroundColor, shadow: fields.addShadow }}
            >
                <Page
                    {...styles.container}
                    className={isMakeImagesMoreTallThanWide ? "layout1" : ""}
                    {...{ backgroundColor: fields.backgroundColor }}
                >
                    {getSection(image1, url1, fields.title1, fields.text1, fields.buttonText1, "left")}
                    {getSection(image2, url2, fields.title2, fields.text2, fields.buttonText2, "right")}
                </Page>
            </StyledWrapper>
        </>
    );
};

const leftSection = {
    displayName: "Left Section",
    sortOrder: 0,
};

const rightSection = {
    displayName: "Right Section",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: WhyMonsma,
    definition: {
        group: "Common",
        icon: "Image",
        displayName: "Why Monsma",
        fieldDefinitions: [
            {
                name: fields.imageUrl1,
                displayName: "Image URL",
                fieldType: "General",
                editorTemplate: "ImagePickerField",
                isRequired: true,
                defaultValue: "",
                tab: leftSection,
            },
            {
                name: fields.title1,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                placeholder: "Title",
                tab: leftSection,
            },
            {
                name: fields.text1,
                displayName: "Section Content",
                defaultValue: "",
                editorTemplate: "MultilineTextField",
                fieldType: "General",
                placeholder: "Multi Line Text ...",
                tab: leftSection,
            },
            {
                name: fields.buttonText1,
                displayName: "Button Text",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tab: leftSection,
            },
            {
                name: fields.link1,
                displayName: "Button/Image Link",
                fieldType: "General",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                tab: leftSection,
            },
            {
                name: fields.imageUrl2,
                displayName: "Image URL",
                fieldType: "General",
                editorTemplate: "ImagePickerField",
                isRequired: true,
                defaultValue: "",
                tab: rightSection,
            },
            {
                name: fields.title2,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                placeholder: "Title",
                tab: rightSection,
            },
            {
                name: fields.text2,
                displayName: "Section Content",
                defaultValue: "",
                editorTemplate: "MultilineTextField",
                fieldType: "General",
                placeholder: "Multi Line Text ...",
                tab: rightSection,
            },
            {
                name: fields.buttonText2,
                displayName: "Button Text",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tab: rightSection,
            },
            {
                name: fields.link2,
                displayName: "Button/Image Link",
                fieldType: "General",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                tab: rightSection,
            },
            {
                name: fields.makeImagesMoreTallThanWide,
                displayName: "Make images more tall than wide",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                name: fields.backgroundColor,
                displayName: "Background Color",
                editorTemplate: "ColorPickerField",
                defaultValue: "",
                fieldType: "General",
                isRequired: true,
            },
            {
                name: fields.addShadow,
                displayName: "Add Shadow",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                isVisible: item => item.fields.makeImagesMoreTallThanWide,
            },
            {
                name: fields.checkboxFieldValue,
                displayName: "Display Center View for Data Boxes",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                isVisible: item => !item.fields.makeImagesMoreTallThanWide,
            },
        ],
    },
};

export default widgetModule;
